import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Star, Loader2, ShieldCheck, MessageSquare, Camera, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { reviewsAPI, authAPI } from '../services/api';
import ReviewPhotoUpload from './ReviewPhotoUpload';
import ReviewPhotos from './ReviewPhotos';
import ReviewHelpfulButton from './ReviewHelpfulButton';
import ReviewReportButton from './ReviewReportButton';
import ReviewShareButtons from './ReviewShareButtons';
import ReviewTranslation from './ReviewTranslation';
import ReviewRewardBanner, { RewardCode } from './ReviewRewardBanner';
import { useT, i18n } from '../services/i18n';

const SORTS = [
  ['recent', 'reviews.sort.recent', (a, b) => new Date(b.created_at) - new Date(a.created_at)],
  ['helpful', 'reviews.sort.helpful', (a, b) => (b.helpful_count || 0) - (a.helpful_count || 0) || new Date(b.created_at) - new Date(a.created_at)],
  ['photos', 'reviews.sort.photos', (a, b) => (b.photos?.length ? 1 : 0) - (a.photos?.length ? 1 : 0) || new Date(b.created_at) - new Date(a.created_at)],
  ['rating_desc', 'reviews.sort.best', (a, b) => b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at)],
  ['rating_asc', 'reviews.sort.worst', (a, b) => a.rating - b.rating || new Date(b.created_at) - new Date(a.created_at)],
];

const Stars = ({ value, size = 16, interactive = false, onChange, hover, onHover }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <button
        key={i}
        type="button"
        disabled={!interactive}
        data-testid={interactive ? `review-star-${i}` : undefined}
        onClick={() => interactive && onChange(i)}
        onMouseEnter={() => interactive && onHover?.(i)}
        onMouseLeave={() => interactive && onHover?.(0)}
        className={interactive ? 'cursor-pointer transition-transform hover:scale-110' : 'cursor-default'}
      >
        <Star
          size={size}
          className={i <= (hover || value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
        />
      </button>
    ))}
  </div>
);

const ReviewsSection = ({ productId, productName }) => {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({ rating: 0, title: '', comment: '', photos: [] });
  const [hover, setHover] = useState(0);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [rewardJustEarned, setRewardJustEarned] = useState(null);
  const [justPosted, setJustPosted] = useState(null);
  const [pending, setPending] = useState(false);
  const [highlight, setHighlight] = useState(false);
  const [sort, setSort] = useState('recent');
  const formRef = useRef(null);
  const { search } = useLocation();
  const wantsReview = new URLSearchParams(search).get('avis') === '1';
  const isAuthenticated = authAPI.isAuthenticated();

  const load = useCallback(async () => {
    try {
      const res = await reviewsAPI.getForProduct(productId);
      setData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isLoading || !wantsReview) return;
    const t = setTimeout(() => {
      (formRef.current || document.getElementById('avis'))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setHighlight(true);
      setTimeout(() => setHighlight(false), 2500);
    }, 150);
    return () => clearTimeout(t);
  }, [isLoading, wantsReview]);

  const submit = async () => {
    setError('');
    if (form.rating === 0) { setError(t('reviews.errRating')); return; }
    if (form.comment.trim().length < 10) { setError(t('reviews.errShort')); return; }
    setSubmitting(true);
    try {
      const created = await reviewsAPI.create(productId, { rating: form.rating, title: form.title, comment: form.comment, photos: form.photos });
      if (created?.pending) { setPending(true); setForm({ rating: 0, title: '', comment: '', photos: [] }); await load(); return; }
      setSuccess(true);
      setRewardJustEarned(created?.reward || null);
      setJustPosted(created || null);
      setForm({ rating: 0, title: '', comment: '', photos: [] });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || t('reviews.errSend'));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-500" /></div>;
  }

  const total = data?.count || 0;
  const withPhotos = data?.reviews?.filter(r => r.photos?.length).length || 0;
  const sortedReviews = [...(data?.reviews || [])].sort(SORTS.find(s => s[0] === sort)?.[2] || SORTS[0][2]);
  const applyVote = (id, res) => setData(d => ({ ...d, reviews: d.reviews.map(r => (r.id === id ? { ...r, helpful_count: res.helpful_count, voted: res.voted } : r)) }));

  return (
    <section id="avis" className="bg-white rounded-xl shadow-sm p-6 scroll-mt-24" data-testid="reviews-section">
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2 flex-wrap">
        <MessageSquare className="text-orange-500" size={22} /> {t('reviews.title')}
        <span className="text-sm font-normal text-gray-500" data-testid="reviews-count">({t('reviews.verifiedCount', { n: total, count: total })})</span>
        {withPhotos > 0 && <span className="text-xs font-medium text-gray-600 bg-gray-100 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="reviews-with-photos"><Camera size={12} /> {t('reviews.withPhotos', { n: withPhotos, count: withPhotos })}</span>}
      </h2>

      <div className="mb-6">
        <ReviewRewardBanner
          productId={productId}
          isAuthenticated={isAuthenticated}
          canReview={!!data?.can_review}
          alreadyReviewed={!!data?.already_reviewed}
          myReward={data?.my_reward}
          onWriteReview={() => { (formRef.current)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); setHighlight(true); setTimeout(() => setHighlight(false), 2500); }}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div>
          <div className="flex items-end gap-3 mb-4">
            <span className="text-5xl font-bold text-gray-900" data-testid="reviews-average">{total ? data.average.toFixed(1) : '–'}</span>
            <div className="pb-2">
              <Stars value={Math.round(data?.average || 0)} size={18} />
              <p className="text-xs text-gray-500 mt-1">{t('reviews.outOf5')}</p>
            </div>
          </div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map(n => {
              const c = data?.distribution?.[String(n)] || 0;
              const pct = total ? Math.round((c / total) * 100) : 0;
              return (
                <div key={n} className="flex items-center gap-2 text-xs text-gray-600">
                  <span className="w-3">{n}</span>
                  <Star size={12} className="fill-yellow-400 text-yellow-400" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-yellow-400" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-6 text-right">{c}</span>
                </div>
              );
            })}
          </div>

          <div className="mt-6">
            {!isAuthenticated ? (
              <p className="text-sm text-gray-500" data-testid="review-login-hint">
                <Link to={`/connexion?redirect=/produit/${productId}`} className="text-blue-600 hover:underline">{t('reviews.login')}</Link> {t('reviews.loginPrompt')}
              </p>
            ) : (pending || data?.my_pending) ? (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="review-pending">
                <ShieldCheck size={16} className="mt-0.5 shrink-0" /> {t('reviews.pendingNotice')}
              </p>
            ) : data?.already_reviewed ? (
              <p className="text-sm text-green-700 flex items-center gap-2" data-testid="review-already">
                <ShieldCheck size={16} /> {t('reviews.already')}
              </p>
            ) : !data?.can_review ? (
              <p className="text-sm text-gray-500" data-testid="review-not-allowed">
                {t('reviews.notAllowed')}
              </p>
            ) : null}
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          {isAuthenticated && data?.can_review && (
            <div ref={formRef} className={`border border-orange-200 bg-orange-50/40 rounded-lg p-5 scroll-mt-24 transition-shadow duration-500 ${highlight ? 'ring-2 ring-orange-400 shadow-lg' : ''}`} data-testid="review-form">
              <h3 className="font-semibold text-gray-900 mb-3">{t('reviews.yourReviewOn', { name: productName })}</h3>
              {wantsReview && <p className="text-xs text-orange-700 bg-orange-100 rounded px-2 py-1 mb-3" data-testid="review-invite-hint">{t('reviews.invite')}</p>}
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 mb-1">{t('reviews.yourRating')}</p>
                <Stars value={form.rating} size={26} interactive onChange={(r) => setForm(f => ({ ...f, rating: r }))} hover={hover} onHover={setHover} />
              </div>
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 mb-1">{t('reviews.titleField')}</p>
                <Input data-testid="review-title-input" value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder={t('reviews.titlePlaceholder')} maxLength={80} />
              </div>
              <div className="mb-3">
                <p className="text-xs font-medium text-gray-600 mb-1">{t('reviews.comment')}</p>
                <textarea
                  data-testid="review-comment-input"
                  value={form.comment}
                  onChange={(e) => setForm(f => ({ ...f, comment: e.target.value }))}
                  rows={4}
                  maxLength={2000}
                  placeholder={t('reviews.commentPlaceholder')}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div className="mb-3">
                <ReviewPhotoUpload photos={form.photos} onChange={(photos) => setForm(f => ({ ...f, photos }))} />
              </div>
              {error && <p className="text-sm text-red-600 mb-3" data-testid="review-error">{error}</p>}
              <Button data-testid="review-submit" onClick={submit} disabled={submitting} className="bg-orange-500 hover:bg-orange-600">
                {submitting && <Loader2 size={16} className="mr-2 animate-spin" />}
                {t('reviews.submit')}
              </Button>
            </div>
          )}
          {success && (
            <div className="space-y-3">
              <p className="text-sm text-green-700 flex items-center gap-2" data-testid="review-success">
                <ShieldCheck size={16} /> {t('reviews.published')}
              </p>
              {rewardJustEarned && <RewardCode reward={rewardJustEarned} />}
              {justPosted?.id && (
                <div className="border border-gray-200 rounded-lg p-3 bg-gray-50" data-testid="review-share-just-posted">
                  <p className="text-xs font-medium text-gray-700 mb-2">{t('reviews.shareMine')}</p>
                  <ReviewShareButtons review={justPosted} productName={productName} compact={false} />
                </div>
              )}
            </div>
          )}

          {total === 0 ? (
            <p className="text-gray-500 text-sm" data-testid="reviews-empty">{t('reviews.empty')}</p>
          ) : (
            <>
            {total > 1 && (
              <div className="flex items-center justify-end gap-2 text-sm">
                <label htmlFor="reviews-sort" className="text-gray-500 text-xs">{t('reviews.sortBy')}</label>
                <select id="reviews-sort" data-testid="reviews-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1 text-xs bg-white focus:outline-none focus:ring-2 focus:ring-orange-500">
                  {SORTS.map(([value, label]) => <option key={value} value={value}>{t(label)}</option>)}
                </select>
              </div>
            )}
            <ul className="divide-y" data-testid="reviews-list">
              {sortedReviews.map(r => (
                <li key={r.id} id={`avis-${r.id}`} className="py-4 scroll-mt-24 target:bg-orange-50/40 target:rounded-lg" data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center text-sm font-semibold">
                        {r.user_name.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{r.user_name}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(r.created_at).toLocaleDateString(i18n.locale(), { day: 'numeric', month: 'long', year: 'numeric' })}
                          {r.verified && <span className="ml-2 inline-flex items-center gap-1 text-green-600"><ShieldCheck size={12} /> {t('reviews.verified')}</span>}
                          {(r.source === 'merchant' || r.source === 'woocommerce') && <span className="ml-2 inline-flex items-center gap-1 text-sky-700" data-testid={`review-merchant-badge-${r.id}`}><Phone size={12} /> {t('reviews.merchantCollected')}</span>}
                        </p>
                      </div>
                    </div>
                    <Stars value={r.rating} size={14} />
                  </div>
                  <ReviewTranslation review={r}>
                    {(title, comment, footer) => (
                      <>
                        {title && <p className="font-medium text-gray-900 text-sm mt-2">{title}</p>}
                        <p className="text-sm text-gray-700 mt-1 whitespace-pre-line" data-testid={`review-comment-${r.id}`}>{comment}</p>
                        {footer}
                      </>
                    )}
                  </ReviewTranslation>
                  <ReviewPhotos photos={r.photos} testId={`review-photos-${r.id}`} />
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <ReviewHelpfulButton review={r} onChange={(res) => applyVote(r.id, res)} />
                    <div className="flex items-center gap-3 flex-wrap">
                      <ReviewShareButtons review={r} productName={productName} />
                      <ReviewReportButton review={r} onReported={() => toast.success(t('reviews.reported'))} />
                    </div>
                  </div>
                  {r.reply && (
                    <div className="mt-3 ml-3 sm:ml-6 border-l-4 border-orange-400 bg-orange-50/70 rounded-r-lg p-3" data-testid={`review-reply-${r.id}`}>
                      <p className="text-xs font-semibold text-gray-900 flex items-center gap-1.5"><ShieldCheck size={13} className="text-orange-500" /> {t('reviews.replyFrom', { author: r.reply.author || 'OTOPIECES.COM' })} <span className="font-normal text-gray-500">· {new Date(r.reply.updated_at || r.reply.created_at).toLocaleDateString(i18n.locale(), { day: 'numeric', month: 'short', year: 'numeric' })}</span></p>
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{r.reply.message}</p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ReviewsSection;
